"use client";

import React from "react";
import styles from "./index.module.css";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { API_DOCS_URL } from "@/constant/common";
import { handleNavClick } from "@/utils/navigation";

type LinkItem = {
  text: string;
  href: string;
  type: string;
};

const Footer = () => {
  const router = useRouter();
  const pathname = usePathname();
  const logoAlt = "Instaticker Logo";
  const descriptionText =
    "Instant communication and data APIs for developers. Go live in minutes, not weeks.";
  const copyrightText = "© 2025 Louvre LLC All rights reserved.";

  const columns = [
    {
      title: "Navigation",
      links: [
        { text: "Products", href: "#products", type: "hash"},
        { text: "How it works", href: "#how-it-works", type: "hash"},
        { text: "Features", href: "#features", type: "hash"},
        { text: "Pricing", href: "#pricing", type: "hash"},
      ],
    },
    {
      title: "Support",
      links: [
        { text: "Privacy Policy", href: "/privacy-policy", type: "page"},
        { text: "Terms of Service", href: "/terms-of-service", type: "page"},
        { 
          text: "API Documentation", 
          href: API_DOCS_URL || "#", 
          type: "blank" 
        },
      ],
    },
  ];

  const renderLink = (link: LinkItem) => {
    if (link.type === "hash") {
      return (
        <a
          href={link.href}
          onClick={(e) => handleNavClick(e, link.href, pathname, router)}
        >
          {link.text}
        </a>
      );
    }

    if (link.type === "blank") {
      return (
        <Link
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {link.text}
        </Link>
      );
    }

    return (
      <Link href={link.href}>
        {link.text}
      </Link>
    );
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.grid}>
        <div className={styles.column}>
          <div className={styles.logo}>
            <Link href="/" scroll={true}>
              <Image
                src="/images/logo-horizontal.svg"
                alt={logoAlt}
                width={100}
                height={30}
              />
            </Link>
          </div>
          <p>{descriptionText}</p>
          <p className={styles.copyText}>{copyrightText}</p>
        </div>
        <div className={styles.linksContainer}>
          {columns.map((column, index) => (
            <div className={styles.column} key={index}>
              <h4>{column.title}</h4>
              <ul>
                {column.links.map((link, i) => (
                  <li key={i}>
                    {renderLink(link)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
